import React, { useState } from 'react';
import { useRouter, Link } from '../../util/router';
import { useAuth } from '../../util/auth';
import { useTeamInvite, useUserOnce, joinTeam } from '../../util/db';
import { useQueryClient } from 'react-query';
import { CheckCircleIcon, XCircleIcon, UserGroupIcon } from '@heroicons/react/24/solid';
import { TEAM_MAX_SEATS } from '../../util/prices';

const JoinTeamSection = () => {
  const router = useRouter();
  const auth = useAuth();
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState(null);

  const queryClient = useQueryClient();
  const code = router.query.code;

  // Fetch the invite
  const {
    status: inviteStatus,
    data: invite,
  } = useTeamInvite(code);

  // Fetch the owner (the person who created the invite)
  const {
    status: ownerStatus,
    data: owner,
  } = useUserOnce(invite?.ownerId);

  // Check if invite is expired
  const isExpired = () => {
    if (!invite?.expiresAt) return false;
    const expiry = invite.expiresAt.toDate ? invite.expiresAt.toDate() : new Date(invite.expiresAt);
    return new Date() > expiry;
  };

  // Check if user is already a member
  const isAlreadyMember = () => {
    if (!auth.user || !owner) return false;
    const teamMembers = owner.teamMembers || [];
    return teamMembers.includes(auth.user.uid);
  };

  // Team owners (with members) cannot join another team — it would hide their own team management UI
  const isTeamOwner = () => {
    if (!auth.user) return false;
    const ownMembers = auth.user.teamMembers || [];
    return ownMembers.filter((uid) => uid !== auth.user.uid).length > 0;
  };

  // Calculate available seats
  const getAvailableSeats = () => {
    if (!owner) return 0;
    // Ten seats for every owner; see TEAM_MAX_SEATS.
    const currentMembers = owner.teamMembers?.length || 0;
    return TEAM_MAX_SEATS - currentMembers;
  };

  // Handle joining the team
  const handleJoin = async () => {
    if (!auth.user || !invite) return;

    setJoining(true);
    setError(null);

    try {
      // Call Cloud Function to join team (handles all validation server-side)
      await joinTeam(code);
      // Invalidate team membership cache so access checks pick up the new membership
      queryClient.invalidateQueries(["team_memberships"]);
      setJoined(true);
    } catch (err) {
      console.error('Error joining team:', err);
      // joinTeam reports an unresolvable owner as an internal/permission error.
      // Those mean the link itself is stale, not that the customer did anything
      // wrong, so say what will actually fix it.
      const raw = err.message || '';
      const linkIsStale =
        raw.includes('Invite is missing owner information') ||
        raw.includes('not created by the team owner');
      const message = linkIsStale
        ? 'This invite link is no longer valid. Please ask your agency owner to create a new invite link from their Teams page and join using that one.'
        : raw || 'Failed to join team. Please try again.';
      setError(message);
    } finally {
      setJoining(false);
    }
  };

  // Loading state
  if (inviteStatus === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invite...</p>
        </div>
      </div>
    );
  }

  // Invalid, deactivated, or minted before the January 2026 teams migration.
  // Those older invites reference a team document that no longer exists and
  // carry no `ownerId`, so joinTeam cannot resolve an owner and rejects them.
  // Catch them here so the customer gets an answer they can act on instead of
  // an internal error after clicking Join.
  const isUnusable = inviteStatus === 'success' && (!invite || !invite.ownerId);

  if (isUnusable) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center max-w-md mx-auto p-8">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">This Invite Link Is No Longer Valid</h1>
          <p className="text-gray-600 mb-6">
            Please ask your agency owner to create a new invite link from their
            Teams page and send it to you, then join using that new link.
          </p>
          <Link to="/" className="text-green-600 hover:text-green-700 font-semibold">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  // Expired
  if (invite && isExpired()) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center max-w-md mx-auto p-8">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invite Link Expired</h1>
          <p className="text-gray-600 mb-6">
            This invite link has expired. Please contact the team owner for a new link.
          </p>
          <Link to="/" className="text-green-600 hover:text-green-700 font-semibold">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  // Inactive
  if (invite && !invite.active) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center max-w-md mx-auto p-8">
          <XCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invite Link Deactivated</h1>
          <p className="text-gray-600 mb-6">
            This invite link is no longer active. Please contact the team owner for a new link.
          </p>
          <Link to="/" className="text-green-600 hover:text-green-700 font-semibold">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  // Successfully joined
  if (joined) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center max-w-md mx-auto p-8">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome to the Team!</h1>
          <p className="text-gray-600 mb-6">
            You've successfully joined <span className="font-semibold">{invite?.ownerName || 'the team'}'s</span> team.
          </p>
          <Link
            to="/dashboard/dashboard"
            className="inline-block bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Waiting for owner data to load
  if (invite && ownerStatus === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading team details...</p>
        </div>
      </div>
    );
  }

  // Team is full
  if (owner && getAvailableSeats() <= 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center max-w-md mx-auto p-8">
          <XCircleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Team is Full</h1>
          <p className="text-gray-600 mb-6">
            <span className="font-semibold">{invite?.ownerName || 'This team'}</span> has reached its member limit.
            Please contact the team owner to add more seats.
          </p>
          <Link to="/" className="text-green-600 hover:text-green-700 font-semibold">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  // Valid invite - show join options
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="text-center max-w-md mx-auto p-8 bg-white rounded-lg shadow-lg border border-gray-200">
        <UserGroupIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Join {invite?.ownerName || 'the team'}'s Team</h1>
        <p className="text-gray-600 mb-6">
          You've been invited to join this team on Insurance Agency Training.
        </p>

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-md mb-4 text-sm">
            {error}
          </div>
        )}

        {auth.user ? (
          // Logged in - show join button or already member message
          isAlreadyMember() ? (
            <div>
              <p className="text-green-600 font-semibold mb-4">You're already a member of this team!</p>
              <Link
                to="/dashboard/dashboard"
                className="inline-block bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700"
              >
                Go to Dashboard
              </Link>
            </div>
          ) : isTeamOwner() ? (
            <div>
              <p className="text-yellow-600 font-semibold mb-2">You own a team and can't join another one.</p>
              <p className="text-sm text-gray-500 mb-4">
                Joining another team would remove access to managing your own team. Remove your team members first if you want to join this team.
              </p>
              <Link
                to="/teams/all"
                className="inline-block bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700"
              >
                Manage My Team
              </Link>
            </div>
          ) : (
            <button
              onClick={handleJoin}
              disabled={joining}
              className="w-full bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700 disabled:opacity-50"
            >
              {joining ? 'Joining...' : 'Join Team'}
            </button>
          )
        ) : (
          // Not logged in - show auth options
          <div className="space-y-3">
            <p className="text-sm text-gray-500 mb-4">Sign in or create an account to join this team.</p>
            <Link
              to={`/auth/signin?next=${encodeURIComponent(`/join/${code}`)}`}
              className="block w-full bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700"
            >
              Sign In
            </Link>
            <Link
              to={`/signup?next=${encodeURIComponent(`/join/${code}`)}`}
              className="block w-full bg-white text-green-600 px-6 py-3 rounded-md font-semibold border-2 border-green-600 hover:bg-green-50"
            >
              Create Account
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinTeamSection;
